<template>
  <div class="settings-page">
    <div class="workspace-header">
      <div class="workspace-title">
        <div class="avatar placeholder">
          {{ getInitials(workspace?.name) }}
        </div>
        <div class="title-info">
          <h2>{{ workspace?.name }}</h2>
          <span class="workspace-id">
            {{ workspaceId }}
            <button class="copy-btn" @click="copyWorkspaceId">
              <CopyIcon />
            </button>
          </span>
        </div>
      </div>
      <div class="workspace-actions">
        <BaseButton kind="auxiliary" text="Open" @click="openWorkspace">
          <template #icon>
            <OpenIcon />
          </template>
        </BaseButton>
        <ButtonMenu
          :offset="[0, 4]"
          placement="bottom-end"
          v-if="canRenameWorkspace || canRemoveWorkspace"
        >
          <template #btn>
            <BaseButton kind="auxiliary">
              <template #icon>
                <MoreIcon />
              </template>
            </BaseButton>
          </template>
          <template #content>
            <BaseButton
              kind="auxiliary"
              text="Rename"
              is-expanded
              :state="canRenameWorkspace ? 'default' : 'disabled'"
              :tooltip="
                canRenameWorkspace
                  ? undefined
                  : 'You are not allowed to perform this action.'
              "
              :tooltip-display="canRenameWorkspace ? 'none' : 'always'"
              @click="renameWorkspace"
            >
              <template #icon>
                <EditIcon />
              </template>
            </BaseButton>
            <BaseButton
              kind="auxiliary"
              text="Delete"
              is-expanded
              :state="canRemoveWorkspace ? 'default' : 'disabled'"
              :tooltip="
                canRemoveWorkspace
                  ? undefined
                  : 'You are not allowed to perform this action.'
              "
              :tooltip-display="canRemoveWorkspace ? 'none' : 'always'"
              @click="deleteWorkspace"
            >
              <template #icon>
                <DeleteIcon />
              </template>
            </BaseButton>
          </template>
        </ButtonMenu>
      </div>
    </div>

    <div class="workspace-content">
      <div class="section">
        <div class="section-header">
          <h3>Members ({{ memberCount }})</h3>
        </div>

        <div class="members-list">
          <WorkspaceMemberRow
            v-for="[memberId, member] in Object.entries(
              workspace?.members || {}
            )"
            :key="memberId"
            :member-id="memberId"
            :organization-id="orgId"
            :role="member.workspace_access_role"
            :can-remove="canRemoveMember(member)"
            :can-set-role="canSetMemberRole(member)"
            :tooltip="'You are not allowed to perform this action.'"
            @remove="removeMember"
            @set-role="setMemberRole"
          />
        </div>

        <div
          class="add-member"
          v-if="canAddWorkspaceMember && availableMembers.length > 0"
        >
          <SearchButtonMenu
            :list="availableMembers"
            list-item-name-property="Title"
            placement="bottom-start"
            @selected-item="({ id }) => addMember(id)"
          >
            <template #btn>
              <BaseButton kind="main" text="Add Member" />
            </template>
            <template #item="{ id }">
              <div class="member-search-item">
                <Avatar
                  :personish="{
                    kind: 'attendee',
                    email: id,
                    name: availableMembers.find((m) => m.id === id)?.Title,
                  }"
                  badge-side="hide"
                />
                <div class="member-info">
                  <div class="name">
                    {{ availableMembers.find((m) => m.id === id)?.Title }}
                  </div>
                </div>
              </div>
            </template>
          </SearchButtonMenu>
        </div>

        <BaseAlert
          state="info"
          layout="center"
          v-if="availableMembers.length === 0"
        >
          All organization members are already in this workspace
        </BaseAlert>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, toRefs } from "vue";

import type {
  WorkspaceAccessRole,
  WorkspaceAction,
  WorkspaceMember,
  WorkspaceMemberAction,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import Avatar from "@/components/elements/Avatar.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import SearchButtonMenu from "@/components/elements/SearchButtonMenu.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import OpenIcon from "@/components/icons/OpenIcon.vue";
import * as inject from "@/components/inject";
import {
  openDeleteWorkspaceModal,
  openRenameWorkspaceModal,
} from "@/components/workspaces";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

import WorkspaceMemberRow from "./WorkspaceMemberRow.vue";

export default defineComponent({
  name: "Workspace",
  components: {
    Avatar,
    BaseButton,
    ButtonMenu,
    BaseAlert,
    CopyIcon,
    DeleteIcon,
    EditIcon,
    MoreIcon,
    OpenIcon,
    SearchButtonMenu,
    WorkspaceMemberRow,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
    workspaceId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { orgId, workspaceId } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();
    inject.currentWorkspaceId(workspaceId);

    const getInitials = (name: string | undefined) => {
      return name
        ? name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase()
            .slice(0, 2)
        : "";
    };

    const [workspace, orgMembersData] = await (async () => {
      const workspaceRef = await controller.workspace.get(workspaceId.value);
      let orgMembersData = null;
      if (orgId.value) {
        const orgMemberIds = await controller.organization.member.all(
          orgId.value
        );
        const orgMemberPromises = orgMemberIds.value.map(
          async (memberId: string) => {
            const memberRef = await controller.organization.member.get(
              orgId.value,
              memberId
            );
            const member = memberRef.value;
            memberRef.release();
            return member ? { id: memberId, name: member.name } : null;
          }
        );
        const orgMembers = await Promise.all(orgMemberPromises);
        orgMembersData = orgMembers.filter((member) => member !== null);
        orgMemberIds.release();
      }
      return [workspaceRef, orgMembersData];
    })();

    // Computed properties for filtering available members
    const availableMembers = computed(() => {
      if (!orgMembersData || !workspace.value?.members) return [];

      const workspaceMemberIds = new Set(Object.keys(workspace.value.members));
      return orgMembersData
        .filter((member) => member && !workspaceMemberIds.has(member.id))
        .map((member) => ({
          id: member.id,
          Title: member.name || member.id.split("@")[0],
        }));
    });

    const memberCount = computed(
      () => Object.keys(workspace.value?.members || {}).length
    );

    // Type-safe permission checker - forces compile-time validation
    const hasPermission = (permission: WorkspaceAction): boolean => {
      return workspace.value?.permissions?.includes(permission) ?? false;
    };

    // Type-safe permission checker - forces compile-time validation
    const hasMemberPermission = (
      member: WorkspaceMember,
      permission: WorkspaceMemberAction
    ): boolean => {
      return member.permissions?.includes(permission) ?? false;
    };

    const canAddWorkspaceMember = computed(() => {
      return hasPermission("member-add");
    });

    const canRenameWorkspace = computed(() => {
      return hasPermission("rename");
    });

    const canRemoveWorkspace = computed(() => {
      return hasPermission("remove");
    });

    const canRemoveMember = (member: WorkspaceMember) => {
      return hasMemberPermission(member, "remove");
    };

    const canSetMemberRole = (member: WorkspaceMember) => {
      return hasMemberPermission(member, "set_role");
    };

    const setMemberRole = async (
      memberId: string,
      role: WorkspaceAccessRole
    ) => {
      await controller.workspace.member.setRole(
        workspaceId.value,
        memberId,
        role
      );
    };

    // Get member details for display
    const getMemberDetails = async (memberId: string) => {
      if (orgId.value) {
        const memberRef = await controller.organization.member.get(
          orgId.value,
          memberId
        );
        const member = memberRef.value;
        memberRef.release();
        return member;
      }
      return null;
    };

    const addMember = async (memberId: string) => {
      await controller.workspace.member.add(
        workspaceId.value,
        "reader-writer",
        memberId
      );
    };

    const removeMember = async (memberId: string) => {
      await controller.workspace.member.remove(workspaceId.value, memberId);
    };

    const renameWorkspace = async () => {
      if (!workspace.value) return;

      openRenameWorkspaceModal(
        workspaceId.value,
        workspace.value.name,
        controller
      );
    };

    const copyWorkspaceId = async () => {
      await copy(workspaceId.value);
      controller.notifications.notify("Workspace ID copied to clipboard");
    };

    const openWorkspace = (event?: MouseEvent) => {
      void navigateTo(
        RouteNames.Workspace,
        router,
        {
          params: { orgId: orgId.value, workspaceId: workspaceId.value },
        },
        event
      );
    };

    const deleteWorkspace = async () => {
      if (!workspace.value) return;

      const orgId = workspace.value.owner;
      const allWorkspaceIds = await controller.workspace.all();
      const orgWorkspaces = await Promise.all(
        allWorkspaceIds.value.map(async (id: string) => {
          const wsRef = await controller.workspace.get(id);
          const ws = wsRef.value;
          wsRef.release();
          return ws && ws.owner === orgId ? ws : null;
        })
      );
      const validWorkspaces = orgWorkspaces.filter((ws) => ws !== null);
      allWorkspaceIds.release();

      if (validWorkspaces.length <= 1) {
        controller.notifications.notify(
          "Cannot delete the last workspace. Each organization must have at least one workspace."
        );
        return;
      }

      openDeleteWorkspaceModal(
        workspaceId.value,
        workspace.value.name,
        controller,
        router,
        orgId
      );
    };

    return {
      addMember,
      copyWorkspaceId,
      openWorkspace,
      removeMember,
      renameWorkspace,
      deleteWorkspace,
      workspace,
      memberCount,
      availableMembers,
      canAddWorkspaceMember,
      canRenameWorkspace,
      canRemoveWorkspace,
      canRemoveMember,
      canSetMemberRole,
      setMemberRole,
      getInitials,
      getMemberDetails,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss" as *;

.workspace-header {
  margin-bottom: 32px;
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workspace-title {
  display: flex;
  align-items: center;
  gap: 16px;

  .avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;

    &.placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 600;

      @include themed() {
        background-color: t($grey-10);
        color: t($grey-60);
      }
    }
  }

  .title-info {
    h2 {
      margin: 0 0 4px 0;
    }

    .workspace-id {
      display: flex;
      align-items: center;
      gap: 4px;
      margin: 0;
      @extend %font-small-regular;

      @include themed() {
        color: t($grey-40);
      }

      .copy-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 2px;
        cursor: pointer;
        opacity: 0.7;
        @include transition(opacity 0.2s ease-out);

        &:hover {
          opacity: 1;
        }

        svg {
          width: 12px;
          height: 12px;
        }
      }
    }
  }
}

.workspace-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workspace-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 32px;
}

.section {
  h3 {
    margin: 0 0 16px 0;

    @include themed() {
      color: t($primary);
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h3 {
    margin: 0;
  }
}

.members-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.member-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-radius: $border-radius-m;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 12px;

  .member-details {
    .name {
      @extend %font-body-medium;
      @include themed() {
        color: t($primary);
      }
    }

    .email {
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }
}

.member-status {
  .status-badge {
    padding: 4px 8px;
    border-radius: $border-radius-m;
    @extend %font-caption-regular;

    @include themed() {
      background-color: t($grey-05);
      color: t($grey-60);
    }
  }
}

.settings-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 8px;

  @include themed() {
    background-color: t($grey-05);
  }

  &.danger {
    @include themed() {
      background-color: t($secondary-20);
    }
  }

  .setting-info {
    .setting-title {
      @extend %font-body-medium;
      margin-bottom: 4px;

      @include themed() {
        color: t($primary);
      }
    }

    .setting-description {
      @extend %font-caption-regular;

      @include themed() {
        color: t($grey-60);
      }
    }
  }
}

.member-search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  width: 100%;

  .member-info {
    .name {
      @extend %font-body-medium;
      @include themed() {
        color: t($primary);
      }
    }

    .email {
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }
}
</style>
